<template>
  <div class="fee-discount-manage-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('feeDiscountManage.page.title')" :subtitle="$t('feeDiscountManage.page.subtitle')" />

    <!-- (2) SearchPanel：筛选字段与含义保持不变，仅重排栅格并统一动作区 -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="feeDiscountManageInfo.conditions.discountId" clearable
            :placeholder="$t('feeDiscountManage.search.discountId')" @keyup.enter.native="_queryFeeDiscountMethod" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="feeDiscountManageInfo.conditions.discountName" clearable
            :placeholder="$t('feeDiscountManage.search.discountName')" @keyup.enter.native="_queryFeeDiscountMethod" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="feeDiscountManageInfo.conditions.discountType" clearable
            :placeholder="$t('feeDiscountManage.search.discountType')" class="w-full">
            <el-option v-for="item in feeDiscountManageInfo.discountTypes" :key="item.statusCd" :label="item.name"
              :value="item.statusCd" />
          </el-select>
        </el-col>
        <!-- 展开的次要筛选条件：保持原有「更多条件」交互 -->
        <el-col :xs="24" :sm="12" :md="8" :lg="6" v-show="feeDiscountManageInfo.moreCondition">
          <el-input v-model.trim="feeDiscountManageInfo.conditions.ruleName" clearable
            :placeholder="$t('feeDiscountManage.search.ruleName')" @keyup.enter.native="_queryFeeDiscountMethod" />
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="_queryFeeDiscountMethod">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="_resetFeeDiscountMethod">
          {{ $t('common.reset') }}
        </el-button>
        <el-button type="text" icon="el-icon-more" @click="_moreCondition">
          {{ feeDiscountManageInfo.moreCondition ? $t('common.hide') : $t('common.more') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="_listFeeDiscounts(page.current, page.size)">
        <template #left>
          <span class="ds-panel__title">{{ $t('feeDiscountManage.list.title') }}</span>
        </template>
        <template #right>
          <el-button type="primary" size="mini" icon="el-icon-plus" @click="_openAddFeeDiscountModal">
            {{ $t('common.add') }}
          </el-button>
        </template>
      </ds-toolbar>

      <ds-data-table :data="feeDiscountManageInfo.feeDiscounts" :loading="loading" :error="hasError"
        :error-message="errorMessage" :density="density" border
        :empty-description="$t('feeDiscountManage.page.empty')" @retry="_listFeeDiscounts(page.current, page.size)">
        <el-table-column prop="discountId" :label="$t('feeDiscountManage.table.discountId')" align="center"
          width="160">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.discountId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="discountName" :label="$t('feeDiscountManage.table.discountName')" align="left"
          min-width="160" show-overflow-tooltip />
        <el-table-column prop="discountTypeName" :label="$t('feeDiscountManage.table.discountType')" align="center"
          width="120" />
        <el-table-column prop="ruleName" :label="$t('feeDiscountManage.table.ruleName')" align="center" width="140"
          show-overflow-tooltip />
        <!-- 规则明细：多行展示，主次分明 -->
        <el-table-column :label="$t('feeDiscountManage.table.rule')" align="left" min-width="220">
          <template slot-scope="scope">
            <div v-if="scope.row.feeDiscountSpecs && scope.row.feeDiscountSpecs.length">
              <div v-for="(item, index) in scope.row.feeDiscountSpecs" :key="index" class="ds-spec-line">
                <span class="ds-spec-line__name">{{ item.specName }}</span>
                <span class="ds-spec-line__value">{{ item.specValue }}</span>
              </div>
            </div>
            <span v-else class="ds-cell-sub">-</span>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" :label="$t('feeDiscountManage.table.createTime')" align="center"
          width="160" />
        <el-table-column :label="$t('common.operation')" align="center" width="180" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="text" @click="_openEditFeeDiscountModel(scope.row)">
              {{ $t('common.edit') }}
            </el-button>
            <el-button size="mini" type="text" class="ds-btn-danger" @click="_openDeleteFeeDiscountModel(scope.row)">
              {{ $t('common.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size" :page-sizes="[10, 20, 30, 50]"
        @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </div>

    <!-- 组件 -->
    <add-fee-discount ref="addFeeDiscount" @success="handleSuccess" />
    <edit-fee-discount ref="editFeeDiscount" @success="handleSuccess" />
    <delete-fee-discount ref="deleteFeeDiscount" @success="handleSuccess" />
  </div>
</template>

<script>
import { getDict, getCommunityId } from '@/api/community/communityApi'
import { queryFeeDiscount } from '@/api/fee/feeDiscountManageApi'
import AddFeeDiscount from '@/components/fee/addFeeDiscount'
import EditFeeDiscount from '@/components/fee/editFeeDiscount'
import DeleteFeeDiscount from '@/components/fee/deleteFeeDiscount'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'FeeDiscountManageList',
  components: {
    AddFeeDiscount,
    EditFeeDiscount,
    DeleteFeeDiscount,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      feeDiscountManageInfo: {
        feeDiscounts: [],
        moreCondition: false,
        discountTypes: [],
        conditions: {
          discountId: '',
          discountName: '',
          discountType: '',
          ruleName: '',
          communityId: ''
        }
      },
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.getCommunityId()
    this.getDictData()
    this._listFeeDiscounts(this.page.current, this.page.size)
  },
  methods: {
    async getCommunityId() {
      try {
        const communityId = await getCommunityId()
        this.feeDiscountManageInfo.conditions.communityId = communityId
      } catch (error) {
        console.error('获取communityId失败:', error)
      }
    },
    async getDictData() {
      try {
        const data = await getDict('fee_discount', 'discount_type')
        this.feeDiscountManageInfo.discountTypes = Array.isArray(data) ? data : []
      } catch (error) {
        console.error('获取字典数据失败:', error)
      }
    },
    async _listFeeDiscounts(page, size) {
      try {
        this.loading = true
        this.hasError = false
        this.errorMessage = ''
        const params = {
          page,
          row: size,
          ...this.feeDiscountManageInfo.conditions
        }
        const res = await queryFeeDiscount(params)

        // 兼容后端在「无匹配数据」时返回 { code:0, records:0 } 而不带 data 字段的情况。
        this.feeDiscountManageInfo.feeDiscounts = Array.isArray(res.data) ? res.data : []
        this.page.total = res.total || 0
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('feeDiscountManage.page.loadFailed')
        console.error('获取折扣列表失败:', error)
      } finally {
        this.loading = false
      }
    },
    _openAddFeeDiscountModal() {
      this.$refs.addFeeDiscount.open()
    },
    _openEditFeeDiscountModel(row) {
      this.$refs.editFeeDiscount.open(row)
    },
    _openDeleteFeeDiscountModel(row) {
      this.$refs.deleteFeeDiscount.open(row)
    },
    _queryFeeDiscountMethod() {
      this.page.current = 1
      this._listFeeDiscounts(this.page.current, this.page.size)
    },
    /**
     * 重置筛选：仅清空可筛选字段，保留 communityId。
     */
    _resetFeeDiscountMethod() {
      this.feeDiscountManageInfo.conditions = {
        ...this.feeDiscountManageInfo.conditions,
        discountName: '',
        discountType: '',
        ruleName: '',
        discountId: ''
      }
      this._listFeeDiscounts(this.page.current, this.page.size)
    },
    _moreCondition() {
      this.feeDiscountManageInfo.moreCondition = !this.feeDiscountManageInfo.moreCondition
    },
    handleSuccess() {
      this._listFeeDiscounts(this.page.current, this.page.size)
    },
    handleSizeChange(val) {
      this.page.size = val
      this.page.current = 1
      this._listFeeDiscounts(this.page.current, this.page.size)
    },
    handleCurrentChange(val) {
      this.page.current = val
      this._listFeeDiscounts(this.page.current, this.page.size)
    }
  }
}
</script>

<style lang="scss" scoped>
.fee-discount-manage-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 规则明细行 */
.ds-spec-line {
  line-height: 1.7;

  &__name {
    color: var(--ds-gray-600);
    margin-right: var(--ds-spacing-xs);
  }

  &__value {
    color: var(--ds-gray-900);
    font-weight: 500;
  }
}

/* 表格次要文字 */
.ds-cell-sub {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
}

/* 危险操作文字按钮 */
.ds-btn-danger {
  color: var(--ds-danger) !important;
}
</style>
